<template>
  <div class="v-viewer" v-loading.large="isLoading">
    <iframe
        ref="iframe"
        class="v-viewer__iframe"
        :src="iframeSrc"
        @load="onLoad"
    ></iframe>

    <div
        class="v-viewer__toolbar"
        v-if="ext === 'pdf' && isToolbarVisible"
    >
      <button class="material-symbols button-icon" @click="download">
        download
      </button>
      <button
          class="material-symbols button-icon"
          @click="$navigateLink(urlFull, { isBlank: true })"
      >
        open_in_new
      </button>
    </div>
  </div>
</template>

<script>
export default {
  emits: ['ready'],
  props: {
    isToolbarVisible: { type: Boolean, default: true },
    url: { type: String, required: true },
    ext: { type: String, default: undefined },
    name: { type: String, required: true },
    aspectRatio: { type: [Number, String], default: 1.414 / 1 },
  },
  data() {
    return {
      isLoading: false,
      src: undefined,
      blob: null,
      retryCount: 0,
      maxRetries: 3,
      loadTimeout: null,
    };
  },
  computed: {
    urlFull() {
      if (this.ext === 'pdf') {
        return `${this.url}?size=pdf#view=fit&navpanes=0&scrollbar=0`;
      }
      if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(this.ext)) {
        return `https://docs.google.com/gview?url=${encodeURIComponent(this.url)}&embedded=true`;
      }
      if (this.url.includes('drive.google.com'))
        return this.url.replace('view?usp=sharing', 'preview');
      if (this.url.includes('docs.google.com'))
        return `https://drive.google.com/file/d/${this.url.split('/')[5]}/preview`;
      return this.url;
    },
    iframeSrc() {
      // Add retry parameter to force gview reload
      return this.retryCount === 0
          ? this.urlFull
          : `${this.urlFull}&retry=${this.retryCount}`;
    },
  },
  async mounted() {
    this.isLoading = true;
    if (this.ext === 'pdf') await this.loadFile();
    this.setupLoadWatcher();
  },
  methods: {
    async loadFile() {
      const res = await fetch(this.url);
      this.blob = await res.blob();
      this.src = URL.createObjectURL(this.blob);
    },
    onLoad() {
      clearTimeout(this.loadTimeout);
      this.isLoading = false;
      this.$emit('ready');
    },
    setupLoadWatcher() {
      // Retry mechanism only for gview and similar embeds
      if (this.ext === 'pdf') return;

      this.loadTimeout = setTimeout(() => {
        this.retryLoad();
      }, 5_000);
    },
    retryLoad() {
      if (this.retryCount >= this.maxRetries) {
        console.warn('gview failed to load after retries');
        this.isLoading = false;
        return;
      }
      console.warn(`Retrying gview load... attempt ${this.retryCount + 1}`);
      this.retryCount++;
      this.isLoading = true;

      this.$nextTick(() => {
        this.setupLoadWatcher();
      });
    },
    download() {
      this.$download(this.blob, 'application/pdf', this.name);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-viewer {
  &__iframe {
    display: block;
    width: 100%;
    height: 100%;
    aspect-ratio: v-bind('aspectRatio');
    border: none;
  }
  &__toolbar {
    margin-top: 1em;
    display: flex;
    gap: 1em;
  }
}
</style>